/**
 * The admin data layer: contract calls in, view models out.
 *
 * Everything the screens import comes from here. Three kinds of work happen:
 *
 *  1. **Straight passthrough** — most endpoints already return what the screen
 *     needs (`QuestionWithContext` carries subject/topic/week, so there is no
 *     client-side join left).
 *  2. **Small joins the API does not do** — `ExamSummary` has `subject_id` but
 *     no subject name, so the exam list is joined against the subject catalog.
 *  3. **Aggregation the API does not serve yet** — `AnalyticsResponse` is
 *     scheduled for v1.1, so the dashboard's weak-topic table and the student
 *     progress screen are composed from `GET /api/admin/attempts` plus one
 *     detail call per attempt. That is fine at this app's scale (one student,
 *     a handful of attempts) and is marked at both call sites so it is easy to
 *     rip out when the endpoint lands.
 */
import { z } from "zod";
import {
  AdminActionsResponse,
  ExamStudentsResponse,
  ItemAnalysisResponse,
  ReviewTriageResponse,
  AdminAttemptDetailResponse,
  AdminUserResponse,
  AdminUsersResponse,
  ApiErrorBody,
  AdminAttemptsResponse,
  Attempt,
  LiveAttemptsResponse,
  ExamDetailResponse,
  ExamResponse,
  ExamsResponse,
  OkResponse,
  OverrideMarksResponse,
  PlaybackManifestResponse,
  PROCTOR_EVENT_SEVERITY,
  ProctorTimelineResponse,
  QuestionResponse,
  QuestionsResponse,
  ReviewQueueResponse,
  SubjectResponse,
  SubjectsResponse,
  TopicResponse,
  TopicsResponse,
} from "@exam/shared";
import type {
  AdminAnswerDetail,
  AutoPickResponse as AutoPickResponseType,
  CorrectAnswer,
  Difficulty,
  OverrideMarksInput,
  PlaybackChunk,
  ProctorEventType,
  ProctorSeverity,
  QuestionListQuery,
  QuestionStatus,
  QuestionType,
  QuestionWithContext,
  RecordingSource,
  ReviewQuestionInput,
  UpdateQuestionInput,
} from "@exam/shared";
import { AutoPickResponse, PLAYBACK_WINDOW_MAX } from "@exam/shared";
import { call } from "./transport";
import { mockEndpoints, mockStudent } from "./mock";
import type {
  AnswerRow,
  AttemptReview,
  Catalog,
  Dashboard,
  ExamRow,
  PhotoItem,
  RecentAttemptRow,
  ScorePoint,
  SeverityCounts,
  StudentProgress,
  SubjectTrend,
  TimelineEvent,
  TopicAccuracyRow,
  Track,
  TrackSegment,
  UpcomingExamRow,
  ViolationHistoryRow,
  WeakTopicRow,
} from "./types";

/* ------------------------------------------------------------------ */
/* Catalog                                                             */
/* ------------------------------------------------------------------ */

export async function fetchCatalog(): Promise<Catalog> {
  const [subjectsRes, topicsRes] = await Promise.all([
    call({
      path: "/api/admin/subjects",
      schema: SubjectsResponse,
      fallback: () => mockEndpoints.subjects(),
    }),
    call({
      path: "/api/admin/topics",
      schema: TopicsResponse,
      fallback: () => mockEndpoints.topics(),
    }),
  ]);

  const weeksBySubject = new Map<string, number[]>();
  for (const topic of topicsRes.topics) {
    if (topic.week_no === null) continue;
    const weeks = weeksBySubject.get(topic.subject_id) ?? [];
    if (!weeks.includes(topic.week_no)) weeks.push(topic.week_no);
    weeksBySubject.set(topic.subject_id, weeks);
  }
  for (const weeks of weeksBySubject.values()) weeks.sort((a, b) => a - b);

  return {
    subjects: subjectsRes.subjects,
    topics: topicsRes.topics,
    subjectById: new Map(subjectsRes.subjects.map((s) => [s.id, s])),
    topicById: new Map(topicsRes.topics.map((t) => [t.id, t])),
    weeksBySubject,
  };
}

/* ------------------------------------------------------------------ */
/* Questions                                                           */
/* ------------------------------------------------------------------ */

export interface QuestionQuery {
  subject_id?: string;
  week_no?: number;
  topic_id?: string;
  type?: QuestionType;
  difficulty?: Difficulty;
  status?: QuestionStatus;
  source?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export async function fetchQuestions(query: QuestionQuery) {
  return call({
    path: "/api/questions",
    query: query as Record<string, string | number | undefined>,
    schema: QuestionsResponse,
    fallback: () => mockEndpoints.questions(query as Record<string, unknown>),
  });
}

/**
 * The review queue. Each row carries server-computed `flags` and human-readable
 * `reasons`, plus queue-wide counts — the screen renders those rather than
 * re-deriving "is this question safe to approve" on the client.
 */
export async function fetchReviewQueue(limit = 200, offset = 0) {
  return call({
    path: "/api/questions/review/queue",
    query: { limit, offset },
    schema: ReviewQueueResponse,
    fallback: () => mockEndpoints.reviewQueue({ limit, offset }),
  });
}

/**
 * Approve or reject. `key_verified` must be sent deliberately — the API rejects
 * approving an unverified NAT key with a 409, and that is the point: upstream
 * publishes truncated decimals, so a click-through approval would put a wrong
 * answer in front of the student.
 */
export async function reviewQuestion(
  id: string,
  input: ReviewQuestionInput,
): Promise<QuestionWithContext> {
  const res = await call({
    method: "POST",
    path: `/api/questions/${id}/review`,
    json: input,
    schema: QuestionResponse,
    fallback: () => mockEndpoints.reviewQuestion(id, input),
  });
  return res.question;
}

/**
 * Pulls an approved question back into the review queue.
 *
 * Offered from item analysis, so that finding a question whose answer key is
 * probably wrong can actually do something about it. Without this the analysis
 * reports the problem and the same question goes onto the next auto-picked
 * paper, to a different cohort. Papers that already contain it are untouched.
 */
export function withdrawQuestion(id: string) {
  return reviewQuestion(id, { action: "withdraw" });
}

/**
 * Uploads a figure for a question. The body is raw image bytes, which the
 * shared `api` helper does not model (it only sends JSON), so this posts
 * directly and normalises the error the same way.
 *
 * If the endpoint is not deployed yet the throw carries the API's own message,
 * which the review screen shows next to the other two ways out.
 */
export async function uploadQuestionFigure(
  id: string,
  file: File,
): Promise<QuestionWithContext> {
  const res = await fetch(`/api/questions/${id}/figure`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });

  const text = await res.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    payload = undefined;
  }

  if (!res.ok) {
    const parsed = ApiErrorBody.safeParse(payload);
    throw new Error(
      parsed.success
        ? parsed.data.error.message
        : `the upload endpoint is not available yet (${res.status})`,
    );
  }

  // The endpoint returns the key and a signed url, but uploading also flips
  // `requires_figure`, so re-read the row rather than patching a local copy.
  return fetchQuestion(id);
}

/** Re-reads one question — used to refresh a signed figure URL that expired. */
export async function fetchQuestion(id: string): Promise<QuestionWithContext> {
  const res = await call({
    path: `/api/questions/${id}`,
    schema: QuestionResponse,
    fallback: () => mockEndpoints.question(id),
  });
  return res.question;
}

/**
 * Removes the stored figure. The API demotes an approved question back to
 * pending when this happens — it still claims to need a figure the student
 * cannot see — and refuses (409) while the question is on a paper.
 */
export async function deleteQuestionFigure(
  id: string,
): Promise<QuestionWithContext> {
  await call({
    method: "DELETE",
    path: `/api/questions/${id}/figure`,
    schema: OkResponse,
    fallback: () => mockEndpoints.deleteFigure(id),
  });
  return fetchQuestion(id);
}

/**
 * Set or clear a topic's lesson video. Blank clears it.
 *
 * Topic-level, because one video usually covers every question in a topic —
 * pasting the same URL onto thirty questions is how it stops getting done.
 */
export async function setTopicVideo(
  topicId: string,
  videoUrl: string,
): Promise<void> {
  await call({
    method: "PATCH",
    path: `/api/admin/topics/${topicId}`,
    json: { video_url: videoUrl.trim() },
    schema: OkResponse,
    fallback: () => ({ ok: true as const }),
  });
}

export async function updateQuestion(
  id: string,
  patch: UpdateQuestionInput,
): Promise<QuestionWithContext> {
  const res = await call({
    method: "PATCH",
    path: `/api/questions/${id}`,
    json: patch,
    schema: QuestionResponse,
    fallback: () =>
      mockEndpoints.patchQuestion(id, patch as Record<string, unknown>),
  });
  return res.question;
}

/* ------------------------------------------------------------------ */
/* Exams                                                               */
/* ------------------------------------------------------------------ */

function toExamRow(
  exam: ExamsResponse["exams"][number],
  catalog: Catalog,
): ExamRow {
  return {
    id: exam.id,
    title: exam.title,
    subject_id: exam.subject_id,
    subject_name: catalog.subjectById.get(exam.subject_id)?.name ?? "—",
    timing_mode: exam.timing_mode,
    syllabus_visible: exam.syllabus_visible,
    collect_answer_sheets: exam.collect_answer_sheets,
    require_phone: exam.require_phone,
    allow_scratchpad: exam.allow_scratchpad,
    scheduled_at: exam.scheduled_at,
    available_from: exam.available_from,
    available_until: exam.available_until,
    duration_min: exam.duration_min,
    min_submit_after_min: exam.min_submit_after_min,
    show_answers_after_submit: exam.show_answers_after_submit,
    covers_weeks: exam.covers_weeks,
    status: exam.status,
    question_count: exam.question_count,
    total_marks: exam.total_marks,
    attempt_status: exam.attempt_status,
    // Carried through rather than defaulted: 0 means EVERYONE and `undefined`
    // means an API that has never heard of assignment. Collapsing the two
    // would make an unknown roster render as "0 students", which is the one
    // reading that is actively wrong.
    assigned_count: exam.assigned_count,
    // The API freezes the paper once the attempt leaves `not_started`.
    frozen:
      exam.attempt_status !== null && exam.attempt_status !== "not_started",
  };
}

export async function fetchExams(
  catalog: Catalog,
  query: { status?: string; subject_id?: string } = {},
): Promise<ExamRow[]> {
  const res = await call({
    path: "/api/exams",
    query: { ...query, limit: 200 },
    schema: ExamsResponse,
    fallback: () => mockEndpoints.exams(query as Record<string, unknown>),
  });
  // Not paged, unlike `fetchAttempts`: exams accumulate far more slowly, and a
  // missing one degrades to an unresolved title rather than a wrong number.
  // Still said out loud, because silence is what made the attempts cap a bug.
  if (res.exams.length < res.total) {
    console.warn(
      `[admin] read ${res.exams.length} of ${res.total} exams — titles for the ` +
        `rest will not resolve.`,
    );
  }
  return res.exams.map((e) => toExamRow(e, catalog));
}

export async function fetchExamDetail(id: string, catalog: Catalog) {
  const res = await call({
    path: `/api/exams/${id}`,
    schema: ExamDetailResponse,
    fallback: () => mockEndpoints.examDetail(id),
  });
  return { exam: toExamRow(res.exam, catalog), questions: res.questions };
}

export async function createExam(input: {
  subject_id: string;
  title: string;
  timing_mode: "from_start" | "scheduled";
  scheduled_at: number | null;
  available_from: number | null;
  available_until: number | null;
  duration_min: number;
  /** Minutes from the student's start before a manual submit unlocks (<= duration). */
  min_submit_after_min: number;
  /** Reveal the answer key + solutions to the student after submit. */
  show_answers_after_submit: boolean;
  covers_weeks: number[];
}) {
  const res = await call({
    method: "POST",
    path: "/api/exams",
    json: input,
    schema: ExamResponse,
    fallback: () => mockEndpoints.createExam(input as Record<string, unknown>),
  });
  return res.exam;
}

export async function attachQuestions(
  examId: string,
  questions: Array<{
    question_id: string;
    marks: number;
    negative_marks: number;
  }>,
  replace = false,
) {
  return call({
    method: "POST",
    path: `/api/exams/${examId}/questions`,
    json: { questions, replace },
    schema: ExamDetailResponse,
    fallback: () =>
      mockEndpoints.attachQuestions(examId, { questions, replace }),
  });
}

export async function autoPick(
  examId: string,
  input: {
    count: number;
    marks: number;
    negative_marks: number;
    type?: QuestionType;
    difficulty?: Difficulty;
    dry_run: boolean;
    replace: boolean;
  },
): Promise<AutoPickResponseType> {
  return call({
    method: "POST",
    path: `/api/exams/${examId}/auto-pick`,
    json: input,
    schema: AutoPickResponse,
    fallback: () => mockEndpoints.autoPick(examId, input),
  });
}

export async function publishExam(
  examId: string,
  body: {
    scheduled_at?: number | null;
    status: "draft" | "scheduled" | "live" | "done";
  },
) {
  const res = await call({
    method: "POST",
    path: `/api/exams/${examId}/publish`,
    json: body,
    schema: ExamResponse,
    fallback: () => mockEndpoints.publishExam(examId, body),
  });
  return res.exam;
}

/* ------------------------------------------------------------------ */
/* Attempts                                                            */
/* ------------------------------------------------------------------ */

/**
 * Every attempt matching the query, paged through rather than truncated.
 *
 * `PageQuery` caps `limit` at 200. This used to ask for 200 and use whatever
 * came back, which was invisible with one student and silently wrong with a
 * cohort: the dashboard and the progress screens are composed from this list,
 * so past 200 attempts they would quietly report on a subset and no number on
 * screen would admit it. The API returns `total`, so there is no excuse for
 * guessing — it is followed to the end.
 *
 * `MAX_ATTEMPT_PAGES` is a runaway guard, not a limit anyone should reach; if
 * it ever trips, the analytics belong on a real endpoint rather than in the
 * browser (see this file's header).
 */
const ATTEMPTS_PAGE_SIZE = 200;
const MAX_ATTEMPT_PAGES = 25;

export async function fetchAttempts(query: { exam_id?: string } = {}) {
  const first = await call({
    path: "/api/admin/attempts",
    query: { ...query, limit: ATTEMPTS_PAGE_SIZE, offset: 0 },
    schema: AdminAttemptsResponse,
    fallback: () => mockEndpoints.attempts(query as Record<string, unknown>),
  });

  const attempts = [...first.attempts];
  for (
    let page = 1;
    attempts.length < first.total && page < MAX_ATTEMPT_PAGES;
    page++
  ) {
    const next = await call({
      path: "/api/admin/attempts",
      query: {
        ...query,
        limit: ATTEMPTS_PAGE_SIZE,
        offset: page * ATTEMPTS_PAGE_SIZE,
      },
      schema: AdminAttemptsResponse,
      fallback: () => mockEndpoints.attempts(query as Record<string, unknown>),
    });
    if (next.attempts.length === 0) break; // defensive: never spin on an empty page
    attempts.push(...next.attempts);
  }

  if (attempts.length < first.total) {
    console.warn(
      `[admin] read ${attempts.length} of ${first.total} attempts — the rest ` +
        `are not included in anything derived from this list.`,
    );
  }

  return { ...first, attempts };
}

/**
 * How long a device may record nothing before what came before it counts as a
 * DIFFERENT occasion rather than a longer lead-in to this one.
 *
 * Setup runs a few minutes; a student who grants the camera and then reads the
 * rules slowly might stretch it to twenty. All of that records continuously,
 * so no real lead-in contains a hole this size. What does contain one is an
 * attempt that was prepared, abandoned, and started again later against the
 * same row.
 */
const IDLE_BREAK_MS = 10 * 60_000;

interface Span {
  start: number;
  end: number;
}

/**
 * The left edge of the timeline: how far back THIS sitting reaches.
 *
 * The timeline used to start at the earliest offset of anything the attempt
 * held — `Math.min(0, ...every chunk, photo and event)`. That is right while
 * everything before t=0 is the setup that led into the exam, which was the only
 * case there was: the phone records from pairing, so a real, observed −61s.
 *
 * It stops being right the moment an attempt row outlives the occasion that
 * created it. `POST /api/attempts/prepare` makes the row so the phone has
 * something to pair against, `ensureAttempt` REUSES a `not_started` row, and
 * offsets are computed from `started_at` at read time — so an abandoned setup
 * from two days ago is stamped at −48.7 hours the moment the exam is finally
 * sat. Taking the minimum then spreads the axis across two days and the exam
 * itself, the thing being reviewed, occupies about a quarter of one percent of
 * the width.
 *
 * So the edge walks BACKWARDS from t=0 through the recording instead, and stops
 * at the first idle gap wider than `IDLE_BREAK_MS`. A continuous lead-in of any
 * length is kept whole; anything on the far side of a real silence belongs to
 * another sitting and is reported separately rather than stretching this one.
 */
function sittingStartMs(
  spans: readonly Span[],
  idleBreakMs = IDLE_BREAK_MS,
): number {
  let edge = 0;
  // Only what lies before the exam start can move the edge, and the walk has
  // to run from the LATEST-ending of those backwards — the gap that matters is
  // the one between a span and the ground already covered, not between
  // neighbours in some other order.
  const before = [...spans]
    .filter((s) => s.start < 0)
    .sort((a, b) => b.end - a.end);

  for (const span of before) {
    if (span.end < edge - idleBreakMs) break;
    edge = Math.min(edge, span.start);
  }
  return edge;
}

/**
 * Chunk runs and the holes between them, on the master timeline.
 *
 * A "run" is a span of chunks the player may concatenate and hand to a
 * `<video>` as one blob. That is bounded by two things: a hole in time, and a
 * change of recorder SESSION — only the first chunk of a session carries the
 * WebM header, so two sessions cannot be decoded as one however adjacent they
 * are. A restarted recorder and the setup-to-exam handover both produce
 * exactly that: a new session with no gap.
 *
 * `startMs` is the left edge of the timeline and is <= 0 whenever any device
 * recorded before the exam began; the leading hole is measured from there, not
 * from zero, so the webcam correctly reads as "not started yet" across the
 * whole setup window instead of appearing to cover it.
 */
function buildSegments(
  chunks: PlaybackChunk[],
  startMs: number,
  endMs: number,
  /**
   * The chunk index this track is expected to open on — 0 normally, and the
   * index after the last set-aside chunk when an earlier occasion has been
   * split off (see `buildTrack`). Anything else means segments were lost.
   */
  baseIndex = 0,
): TrackSegment[] {
  const segments: TrackSegment[] = [];
  if (chunks.length === 0) {
    return endMs > startMs
      ? [
          {
            kind: "gap",
            start_ms: startMs,
            end_ms: endMs,
            chunks: [],
            decodable: true,
          },
        ]
      : [];
  }

  // Ordered by (session, chunk_index), NOT by offset: `chunk_index` is the
  // authoritative byte order of a WebM stream, while `offset_ms` derives from
  // a server clock that can be re-synced. Two chunks swapped inside a run make
  // it undecodable, so the ordering that decides the bytes must come from the
  // sequence number. Offsets are still what the gap arithmetic below uses.
  const sorted = [...chunks].sort(
    (a, b) =>
      (a.session_idx ?? 0) - (b.session_idx ?? 0) ||
      a.chunk_index - b.chunk_index,
  );
  // A device that paired late leaves a leading hole. It is real signal — the
  // contract is explicit that it must be shown, not normalised away.
  if (sorted[0]!.offset_ms > startMs) {
    segments.push({
      kind: "gap",
      start_ms: startMs,
      end_ms: sorted[0]!.offset_ms,
      chunks: [],
      decodable: true,
    });
  }

  // Chunk boundaries never land exactly; treat sub-second holes as continuous.
  const JOIN_TOLERANCE_MS = 750;
  let run: PlaybackChunk[] = [];

  // The chunk index the next run must open on to be decodable. Advanced past
  // every chunk actually present, so a hole anywhere shows up as the run after
  // it failing to line up.
  let expectedIndex = baseIndex;

  const flush = () => {
    if (run.length === 0) return;
    segments.push({
      kind: "chunks",
      start_ms: run[0]!.offset_ms,
      end_ms: run[run.length - 1]!.offset_ms + run[run.length - 1]!.duration_ms,
      chunks: run,
      decodable: run[0]!.chunk_index === expectedIndex,
    });
    expectedIndex = run[run.length - 1]!.chunk_index + 1;
    run = [];
  };

  for (const chunk of sorted) {
    if (run.length > 0) {
      const prev = run[run.length - 1]!;
      const expected = prev.offset_ms + prev.duration_ms;

      // A change of session ALWAYS ends a run, gap or no gap. Only the first
      // blob of a MediaRecorder session carries the WebM header, so chunks
      // from two sessions cannot be concatenated and decoded as one — and a
      // recorder that restarts itself resumes immediately, leaving no gap in
      // time to notice it by. Splitting on time alone put the second session's
      // header in the middle of a run and made everything after it
      // undecodable.
      const newSession = (chunk.session_idx ?? 0) !== (prev.session_idx ?? 0);

      if (newSession || chunk.offset_ms - expected > JOIN_TOLERANCE_MS) {
        flush();
        // Only a real hole in time is a gap the reviewer should see. A session
        // change with no hole is invisible to them, and should be — the
        // footage is continuous, it just needs decoding separately.
        if (chunk.offset_ms - expected > JOIN_TOLERANCE_MS) {
          segments.push({
            kind: "gap",
            start_ms: expected,
            end_ms: chunk.offset_ms,
            chunks: [],
            decodable: true,
          });
        }
      }
    }
    run.push(chunk);
  }
  flush();

  const last = segments[segments.length - 1];
  if (last && last.end_ms < endMs - JOIN_TOLERANCE_MS) {
    segments.push({
      kind: "gap",
      start_ms: last.end_ms,
      end_ms: endMs,
      chunks: [],
      decodable: true,
    });
  }
  return segments;
}

function severityOf(events: TimelineEvent[]): SeverityCounts {
  const out: SeverityCounts = { info: 0, warn: 0, critical: 0 };
  for (const e of events) out[e.severity]++;
  return out;
}

function byType(
  counts: Partial<Record<ProctorEventType, number>>,
): Array<{ type: ProctorEventType; count: number; severity: ProctorSeverity }> {
  return (Object.entries(counts) as Array<[ProctorEventType, number]>)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({
      type,
      count,
      severity: PROCTOR_EVENT_SEVERITY[type],
    }))
    .sort((a, b) => b.count - a.count);
}

function toAnswerRow(a: AdminAnswerDetail): AnswerRow {
  return { ...a, overridden: a.marks_override !== null };
}

/**
 * The playback manifest is WINDOWED: one request signs at most
 * `PLAYBACK_WINDOW_MAX` chunks per source, so a long attempt is split across
 * pages (`?from=&limit=`, cursor `next_from`). We page through EVERY window and
 * reassemble the complete per-source chunk list here.
 *
 * Why reassemble rather than play one window at a time: every chunk after
 * index 0 is a *headerless* WebM fragment (a single `MediaRecorder` stream —
 * only chunk 0 carries the header). A window that does not start at chunk 0 is
 * therefore undecodable on its own, and a plain `<video>` cannot seek across
 * the byte gap that prepending just the header would leave. The player already
 * concatenates a source's chunks from chunk 0 into one seekable Blob, so the
 * correct, browser-safe consumption of the paged API is to gather all windows
 * and hand the player the whole recording — which also keeps playback smooth
 * (one Blob, no window-boundary re-seeks). The server's per-request signing cap
 * is still honoured; only the client does more requests.
 *
 * The first window carries the whole-attempt totals (used for the scrubber);
 * the remaining windows are fetched in parallel — chunk counts are fixed for a
 * submitted attempt.
 */
async function fetchFullPlaybackManifest(
  id: string,
): Promise<PlaybackManifestResponse> {
  const limit = PLAYBACK_WINDOW_MAX;
  const fetchWindow = (from: number) =>
    call({
      path: `/api/admin/attempts/${id}/playback`,
      query: { from, limit },
      schema: PlaybackManifestResponse,
      fallback: () => mockEndpoints.playbackManifest(id, { from, limit }),
    });

  const first = await fetchWindow(0);
  const maxTotal = first.sources.reduce(
    (max, s) => Math.max(max, s.total_chunks),
    0,
  );

  const laterFroms: number[] = [];
  for (let from = limit; from < maxTotal; from += limit) laterFroms.push(from);
  const later = await Promise.all(laterFroms.map(fetchWindow));

  // Merge each source's windows back into one chunk list, in chunk_index order.
  const chunksBySource = new Map<RecordingSource, PlaybackChunk[]>();
  for (const page of [first, ...later]) {
    for (const s of page.sources) {
      const list = chunksBySource.get(s.source) ?? [];
      list.push(...s.chunks);
      chunksBySource.set(s.source, list);
    }
  }

  return {
    ...first,
    // The window echo is meaningless once reassembled: report the full span.
    window: { from: 0, limit: Math.max(1, maxTotal) },
    sources: first.sources.map((s) => ({
      ...s,
      has_more: false,
      next_from: null,
      chunks: (chunksBySource.get(s.source) ?? [])
        .slice()
        .sort((a, b) => a.chunk_index - b.chunk_index),
    })),
  };
}

/**
 * One device's track, split on the timeline's left edge.
 *
 * Everything before that edge belongs to an earlier occasion against the same
 * attempt row (see `sittingStartMs`). It is taken off the timeline — otherwise
 * it stretches the axis until the exam is unreadable — but it is COUNTED, not
 * dropped: a pane reading "No recording" for a device holding megabytes of
 * video is the exact wrong conclusion for a reviewer to reach.
 */
function buildTrack(
  source: RecordingSource,
  found:
    | {
        chunks: PlaybackChunk[];
        first_offset_ms: number;
        total_duration_ms: number;
        total_bytes: number;
      }
    | undefined,
  startMs: number,
  durationMs: number,
  startedAt: number | null,
): Track {
  const all = found?.chunks ?? [];

  // A chunk that STRADDLES the edge is kept — it is part of this sitting, it
  // merely began a moment before the edge was drawn.
  const chunks = all.filter((c) => c.offset_ms + c.duration_ms > startMs);
  const earlierChunks = all.filter(
    (c) => c.offset_ms + c.duration_ms <= startMs,
  );
  const last = earlierChunks.at(-1);
  /** Where this sitting's footage must start for its first run to decode. */
  const baseIndex = last === undefined ? 0 : last.chunk_index + 1;

  return {
    source,
    chunks,
    segments: buildSegments(chunks, startMs, durationMs, baseIndex),
    first_offset_ms: chunks[0]?.offset_ms ?? found?.first_offset_ms ?? 0,
    total_duration_ms: found?.total_duration_ms ?? 0,
    total_bytes: found?.total_bytes ?? 0,
    // Only the first chunk of a recorder session carries the WebM header, so
    // footage that does not begin where it should cannot be decoded at all.
    //
    // Measured against the chunk BEFORE the split, not against 0: when an
    // earlier sitting has been set aside, this sitting legitimately starts at
    // index 33 rather than 0, and comparing to 0 would stamp "Footage
    // uploaded, but unplayable" on a recording that plays perfectly well. What
    // is still caught is the real fault — a HOLE at the boundary, meaning the
    // chunks carrying this session's header never arrived.
    header_missing:
      chunks.length > 0 && (chunks[0]?.chunk_index ?? 0) !== baseIndex,
    earlier:
      earlierChunks.length === 0 || last === undefined
        ? null
        : {
            chunks: earlierChunks.length,
            duration_ms: earlierChunks.reduce(
              (sum, c) => sum + c.duration_ms,
              0,
            ),
            ended_at_ms:
              startedAt !== null
                ? startedAt + last.offset_ms + last.duration_ms
                : last.offset_ms + last.duration_ms,
          },
  };
}

/**
 * The attempt review screen in one shot: grading detail, the proctor timeline
 * and the pre-signed playback manifest. Three parallel calls rather than one
 * fat endpoint, because the manifest carries ~700 chunk URLs and is the only
 * part with a TTL.
 */
export async function fetchAttemptReview(id: string): Promise<AttemptReview> {
  const [detail, timeline, manifest] = await Promise.all([
    call({
      path: `/api/admin/attempts/${id}`,
      schema: AdminAttemptDetailResponse,
      fallback: () => mockEndpoints.attemptDetail(id),
    }),
    call({
      path: `/api/admin/attempts/${id}/events`,
      schema: ProctorTimelineResponse,
      fallback: () => mockEndpoints.attemptEvents(id),
    }),
    fetchFullPlaybackManifest(id),
  ]);

  const events: TimelineEvent[] = [...timeline.events].sort(
    (a, b) => a.offset_ms - b.offset_ms,
  );

  // The timeline must cover everything there is to see: the graded window, the
  // recordings, and any event logged after submit.
  const durationMs = Math.max(
    detail.duration_ms,
    manifest.duration_ms,
    ...manifest.sources.flatMap((s) =>
      s.chunks.map((c) => c.offset_ms + c.duration_ms),
    ),
    ...events.map((e) => e.offset_ms),
    1,
  );

  // …and everything before it that belongs to THIS sitting. The phone is
  // paired, placed, and used for the paper check and room scan before the exam
  // starts, so its first chunks and those photos carry negative offsets — a
  // real, observed −61s in one attempt. The timeline extends left to hold them
  // rather than clamping them onto t=0, but only as far back as the recording
  // actually runs: see `sittingStartMs` for what stops it.
  const startMs = sittingStartMs([
    ...manifest.sources.flatMap((s) =>
      s.chunks.map((c) => ({
        start: c.offset_ms,
        end: c.offset_ms + c.duration_ms,
      })),
    ),
    ...manifest.photos.map((p) => ({ start: p.offset_ms, end: p.offset_ms })),
    ...events.map((e) => ({ start: e.offset_ms, end: e.offset_ms })),
  ]);

  const toTrack = (source: RecordingSource): Track =>
    buildTrack(
      source,
      manifest.sources.find((s) => s.source === source),
      startMs,
      durationMs,
      detail.attempt.started_at,
    );

  // Webcam and phone are always shown — an empty one reads as "nothing
  // recorded", which is itself worth seeing. The screen recording is newer, so
  // only surface it when the manifest actually carries screen chunks; older
  // attempts (no screen source) must not sprout an empty third pane.
  const tracks: Track[] = [toTrack("webcam"), toTrack("phone")];
  const screen = manifest.sources.find((s) => s.source === "screen");
  if (screen && screen.chunks.length > 0) tracks.push(toTrack("screen"));

  const photos: PhotoItem[] = manifest.photos
    .map((p) => ({
      id: p.id,
      kind: p.kind,
      url: p.url,
      created_at: p.created_at,
      offset_ms: p.offset_ms,
    }))
    .sort((a, b) => a.offset_ms - b.offset_ms);

  return {
    attempt_id: detail.attempt.id,
    exam_id: detail.attempt.exam_id,
    exam_title: detail.exam_title,
    subject_name: detail.subject_name,
    student_name: detail.student_name,
    student_id: detail.attempt.user_id,
    status: detail.attempt.status,
    started_at: detail.attempt.started_at,
    submitted_at: detail.attempt.submitted_at,
    score: detail.attempt.score,
    max_score: detail.attempt.max_score,
    attempt_no: detail.attempt.attempt_no,
    extra_time_min: detail.attempt.extra_time_min,
    rejoin_count: detail.attempt.rejoin_count,
    offline_ms: detail.attempt.offline_ms,
    start_ms: startMs,
    duration_ms: durationMs,
    answers: detail.answers
      .map(toAnswerRow)
      .sort((a, b) => a.order_idx - b.order_idx),
    events,
    tracks,
    photos,
    severity: severityOf(events),
    total_violations: timeline.total_violations,
    by_type: byType(timeline.violation_counts),
    playback_expires_at: manifest.expires_at,
  };
}

/** Manual mark correction. `marks_awarded: null` restores the auto score. */
export async function overrideMarks(
  attemptId: string,
  input: OverrideMarksInput,
) {
  const res = await call({
    method: "POST",
    path: `/api/admin/attempts/${attemptId}/override`,
    json: input,
    schema: OverrideMarksResponse,
    fallback: () => mockEndpoints.overrideMarks(attemptId, input),
  });
  return {
    answer: toAnswerRow(res.answer),
    score: res.score,
    max_score: res.max_score,
  };
}

/* ------------------------------------------------------------------ */
/* Aggregates composed client-side (until AnalyticsResponse ships)     */
/* ------------------------------------------------------------------ */

interface TopicAcc {
  topic_id: string;
  topic_name: string;
  subject_name: string;
  week_no: number | null;
  answered: number;
  correct: number;
  time_ms: number;
}

/** Rolls every graded answer of every submitted attempt up by topic. */
function accumulateTopics(details: AdminAttemptDetailResponse[]) {
  const acc = new Map<string, TopicAcc>();
  for (const detail of details) {
    for (const a of detail.answers) {
      // Unanswered questions are stored as is_correct=false; counting them
      // would understate accuracy on topics they simply ran out of time for.
      if (a.response === null) continue;
      const q = a.question;
      const cur = acc.get(q.topic_id) ?? {
        topic_id: q.topic_id,
        topic_name: q.topic_name,
        subject_name: q.subject_name,
        week_no: q.week_no,
        answered: 0,
        correct: 0,
        time_ms: 0,
      };
      cur.answered++;
      if (a.is_correct) cur.correct++;
      cur.time_ms += a.time_spent_ms;
      acc.set(q.topic_id, cur);
    }
  }
  return acc;
}

async function fetchAllAttemptDetails(): Promise<AdminAttemptDetailResponse[]> {
  const list = await fetchAttempts();
  const graded = list.attempts.filter(
    (a) =>
      a.attempt.status === "submitted" || a.attempt.status === "auto_submitted",
  );
  return Promise.all(
    graded.map((a) =>
      call({
        path: `/api/admin/attempts/${a.attempt.id}`,
        schema: AdminAttemptDetailResponse,
        fallback: () => mockEndpoints.attemptDetail(a.attempt.id),
      }),
    ),
  );
}

export async function fetchDashboard(catalog: Catalog): Promise<Dashboard> {
  const [exams, attempts] = await Promise.all([
    fetchExams(catalog),
    fetchAttempts(),
  ]);

  const upcoming: UpcomingExamRow[] = exams
    .filter(
      (e) =>
        e.status === "draft" || e.status === "scheduled" || e.status === "live",
    )
    .sort((a, b) => {
      // Unscheduled drafts sort last — there is no date to act on.
      if (a.scheduled_at === null) return 1;
      if (b.scheduled_at === null) return -1;
      return a.scheduled_at - b.scheduled_at;
    })
    .map((e) => ({
      exam_id: e.id,
      title: e.title,
      subject_name: e.subject_name,
      timing_mode: e.timing_mode,
      scheduled_at: e.scheduled_at,
      duration_min: e.duration_min,
      question_count: e.question_count,
      total_marks: e.total_marks,
      status: e.status,
    }));

  const recent: RecentAttemptRow[] = attempts.attempts
    .slice()
    .sort(
      (a, b) => (b.attempt.submitted_at ?? 0) - (a.attempt.submitted_at ?? 0),
    )
    .map((a) => ({
      attempt_id: a.attempt.id,
      exam_title: a.exam_title,
      student_name: a.student_name,
      submitted_at: a.attempt.submitted_at,
      status: a.attempt.status,
      score: a.attempt.score,
      max_score: a.attempt.max_score,
      total_violations: a.total_violations,
    }));

  // v1.1: replace with GET /api/admin/analytics.
  const details = await fetchAllAttemptDetails();
  const weak_topics: WeakTopicRow[] = [...accumulateTopics(details).values()]
    .map((t) => ({
      topic_id: t.topic_id,
      topic_name: t.topic_name,
      subject_name: t.subject_name,
      week_no: t.week_no,
      answered: t.answered,
      correct: t.correct,
      accuracy: t.answered === 0 ? 0 : t.correct / t.answered,
    }))
    .sort((a, b) => a.accuracy - b.accuracy || b.answered - a.answered)
    .slice(0, 8);

  return { upcoming, recent, weak_topics, aggregated_from: details.length };
}

export async function fetchStudentProgress(
  studentId: string,
  catalog: Catalog,
): Promise<StudentProgress> {
  const [list, exams] = await Promise.all([
    fetchAttempts(),
    fetchExams(catalog),
  ]);
  const examById = new Map(exams.map((e) => [e.id, e]));

  const mine = list.attempts.filter(
    (a) =>
      a.attempt.user_id === studentId &&
      (a.attempt.status === "submitted" ||
        a.attempt.status === "auto_submitted"),
  );

  const details = await Promise.all(
    mine.map((a) =>
      call({
        path: `/api/admin/attempts/${a.attempt.id}`,
        schema: AdminAttemptDetailResponse,
        fallback: () => mockEndpoints.attemptDetail(a.attempt.id),
      }),
    ),
  );

  const timelines = await Promise.all(
    mine.map((a) =>
      call({
        path: `/api/admin/attempts/${a.attempt.id}/events`,
        schema: ProctorTimelineResponse,
        fallback: () => mockEndpoints.attemptEvents(a.attempt.id),
      }),
    ),
  );

  // One trend line per subject — small multiples rather than one multi-series
  // chart, so no chart ever needs a categorical palette.
  const bySubject = new Map<string, ScorePoint[]>();
  for (const a of mine) {
    if (a.attempt.submitted_at === null) continue;
    const exam = examById.get(a.attempt.exam_id);
    const subjectName = exam?.subject_name ?? "—";
    const max = a.attempt.max_score ?? 0;
    const points = bySubject.get(subjectName) ?? [];
    points.push({
      attempt_id: a.attempt.id,
      exam_id: a.attempt.exam_id,
      exam_title: a.exam_title,
      submitted_at: a.attempt.submitted_at,
      score: a.attempt.score ?? 0,
      max_score: max,
      ratio: max > 0 ? Math.max(0, (a.attempt.score ?? 0) / max) : 0,
      attempt_no: a.attempt.attempt_no,
      time_taken_ms:
        a.attempt.started_at === null
          ? null
          : a.attempt.submitted_at - a.attempt.started_at,
      // The allowance the student actually had, extra time included — comparing
      // time taken against the paper's nominal duration would show someone with
      // an accommodation as having overrun when they did not.
      time_allowed_ms:
        ((exam?.duration_min ?? 0) + a.attempt.extra_time_min) * 60_000,
    });
    bySubject.set(subjectName, points);
  }

  const trends: SubjectTrend[] = [...bySubject.entries()]
    .map(([subject_name, points]) => ({
      subject_name,
      points: points.sort((a, b) => a.submitted_at - b.submitted_at),
    }))
    .sort((a, b) => a.subject_name.localeCompare(b.subject_name));

  const topics: TopicAccuracyRow[] = [...accumulateTopics(details).values()]
    .map((t) => ({
      topic_id: t.topic_id,
      topic_name: t.topic_name,
      subject_name: t.subject_name,
      week_no: t.week_no,
      answered: t.answered,
      correct: t.correct,
      accuracy: t.answered === 0 ? 0 : t.correct / t.answered,
      avg_time_ms: t.answered === 0 ? 0 : Math.round(t.time_ms / t.answered),
    }))
    .sort(
      (a, b) =>
        a.accuracy - b.accuracy ||
        (a.week_no ?? 99) - (b.week_no ?? 99) ||
        a.topic_name.localeCompare(b.topic_name),
    );

  const violations: ViolationHistoryRow[] = mine.map((a, i) => {
    const timeline = timelines[i];
    const events = (timeline?.events ?? []) as TimelineEvent[];
    return {
      attempt_id: a.attempt.id,
      exam_title: a.exam_title,
      submitted_at: a.attempt.submitted_at,
      severity: severityOf(events),
      total: timeline?.total_violations ?? 0,
      top_types: byType(timeline?.violation_counts ?? {})
        .slice(0, 3)
        .map(({ type, count }) => ({ type, count })),
      rejoin_count: a.attempt.rejoin_count,
      offline_ms: a.attempt.offline_ms,
    };
  });
  violations.sort((a, b) => (b.submitted_at ?? 0) - (a.submitted_at ?? 0));

  return {
    student_id: studentId,
    student_name: mine[0]?.student_name ?? mockStudent.name,
    attempt_count: mine.length,
    trends,
    topics,
    violations,
  };
}

/** The single student's id, for the dashboard's link to the progress screen. */
export async function resolveStudentId(): Promise<string | null> {
  const list = await fetchAttempts();
  return list.attempts[0]?.attempt.user_id ?? null;
}

export type { CorrectAnswer, QuestionListQuery };

/**
 * Exported for its own tests. `buildSegments` decides where a recording can be
 * cut and decoded, and getting it wrong produces silently unplayable video
 * rather than an error, so it is tested directly rather than through a screen.
 */
export const __test = {
  buildSegments,
  sittingStartMs,
  buildTrack,
  IDLE_BREAK_MS,
};

/* ------------------------------------------------------------------ */
/* Item analysis & review triage                                       */
/* ------------------------------------------------------------------ */

/**
 * What the responses say about the QUESTIONS, rather than about a student.
 * No fixture fallback: an invented mis-keyed-question warning would send
 * someone to rewrite a question that is fine.
 */
export function fetchItemAnalysis(
  query: { examId?: string; flaggedOnly?: boolean } = {},
) {
  return call({
    path: "/api/admin/item-analysis",
    query: {
      exam_id: query.examId,
      flagged_only: query.flaggedOnly ? "true" : undefined,
    },
    schema: ItemAnalysisResponse,
  });
}

/**
 * Which ATTEMPTS are worth watching, ranked, with the evidence for each.
 *
 * Named `Triage` rather than `ReviewQueue` because this file already has a
 * `fetchReviewQueue` — the queue of QUESTIONS awaiting human review. Two
 * different queues, and confusing them would send an admin to the wrong screen.
 */
export function fetchReviewTriage(
  query: { examId?: string; limit?: number } = {},
) {
  return call({
    path: "/api/admin/review-queue",
    query: { exam_id: query.examId, limit: query.limit ?? 50 },
    schema: ReviewTriageResponse,
  });
}

/* ------------------------------------------------------------------ */
/* Exam roster                                                         */
/* ------------------------------------------------------------------ */

export function fetchExamStudents(examId: string) {
  return call({
    path: `/api/exams/${examId}/students`,
    schema: ExamStudentsResponse,
  });
}

/**
 * Replaces the roster wholesale. An EMPTY list means every student — that is
 * the default every exam has, not "nobody".
 */
export function setExamStudents(examId: string, studentIds: string[]) {
  return call({
    method: "PUT",
    path: `/api/exams/${examId}/students`,
    json: { student_ids: studentIds },
    schema: ExamStudentsResponse,
  });
}

/* ------------------------------------------------------------------ */
/* Students                                                            */
/* ------------------------------------------------------------------ */

/**
 * No fallback fixtures on any of these. A roster screen that quietly shows
 * invented students, or an intervention that silently does nothing, is worse
 * than an error message — these change who can sit an exam and what a graded
 * record says.
 */
export function fetchUsers(
  query: {
    role?: "admin" | "student";
    active?: boolean;
    q?: string;
    limit?: number;
    offset?: number;
  } = {},
) {
  return call({
    path: "/api/admin/users",
    query: {
      role: query.role,
      active: query.active,
      q: query.q,
      limit: query.limit ?? 200,
      offset: query.offset ?? 0,
    },
    schema: AdminUsersResponse,
  });
}

export function createStudent(input: {
  email: string;
  name: string;
  password: string;
  role?: "admin" | "student";
}) {
  return call({
    method: "POST",
    path: "/api/admin/users",
    json: { role: "student", ...input },
    schema: AdminUserResponse,
  }).then((r) => r.user);
}

export function updateStudent(
  id: string,
  patch: { email?: string; name?: string; password?: string; active?: boolean },
) {
  return call({
    method: "PATCH",
    path: `/api/admin/users/${id}`,
    json: patch,
    schema: AdminUserResponse,
  }).then((r) => r.user);
}

/* ------------------------------------------------------------------ */
/* Live monitor                                                        */
/* ------------------------------------------------------------------ */

export function fetchLiveAttempts(examId?: string) {
  return call({
    path: "/api/admin/live",
    query: { exam_id: examId },
    schema: LiveAttemptsResponse,
  });
}

/* ------------------------------------------------------------------ */
/* Interventions                                                       */
/* ------------------------------------------------------------------ */

const AttemptActionResponse = z.object({
  attempt: Attempt,
  deadline_ms: z.number().nullable().optional(),
  remaining_ms: z.number().optional(),
  granted_extra_minutes: z.number().optional(),
  score: z.number().optional(),
  max_score: z.number().optional(),
  media_objects_deleted: z.number().optional(),
});
export type AttemptActionResult = z.infer<typeof AttemptActionResponse>;

export function extendAttempt(
  id: string,
  minutes: number,
  note: string | null,
) {
  return call({
    method: "POST",
    path: `/api/admin/attempts/${id}/extend`,
    json: { minutes, note },
    schema: AttemptActionResponse,
  });
}

export function forceSubmitAttempt(id: string, note: string | null) {
  return call({
    method: "POST",
    path: `/api/admin/attempts/${id}/force-submit`,
    json: { note },
    schema: AttemptActionResponse,
  });
}

export function reopenAttempt(
  id: string,
  extraMinutes: number,
  note: string | null,
) {
  return call({
    method: "POST",
    path: `/api/admin/attempts/${id}/reopen`,
    json: { extra_minutes: extraMinutes, note },
    schema: AttemptActionResponse,
  });
}

/**
 * `expect` is not optional in practice, only in the contract.
 *
 * Reset permanently deletes a sitting's answers, score, proctor events and
 * every recording it produced, and it is fired from a list that may have been
 * open for an hour. Sending what the screen believed it was destroying turns a
 * stale row into a 409 instead of the wrong student's exam being wiped with a
 * 200. Every caller in this app passes it.
 */
export function resetAttempt(
  id: string,
  note: string | null,
  expect: { studentId: string; status: string },
) {
  return call({
    method: "POST",
    path: `/api/admin/attempts/${id}/reset`,
    json: {
      note,
      expect_student_id: expect.studentId,
      expect_status: expect.status,
    },
    schema: AttemptActionResponse,
  });
}

export function grantRetake(
  examId: string,
  studentId: string,
  note: string | null,
) {
  return call({
    method: "POST",
    path: `/api/admin/exams/${examId}/retake`,
    json: { student_id: studentId, note },
    schema: z.object({ attempt: Attempt }),
  }).then((r) => r.attempt);
}

export function extendExam(
  examId: string,
  minutes: number,
  opts: { extendWindow?: boolean; note?: string | null } = {},
) {
  return call({
    method: "POST",
    path: `/api/admin/exams/${examId}/extend`,
    json: {
      minutes,
      extend_window: opts.extendWindow ?? true,
      note: opts.note ?? null,
    },
    schema: z.object({
      duration_min: z.number(),
      available_until: z.number().nullable(),
    }),
  });
}

export function fetchAttemptActions(attemptId: string) {
  return call({
    path: `/api/admin/attempts/${attemptId}/actions`,
    schema: AdminActionsResponse,
  }).then((r) => r.actions);
}

export function fetchExamActions(examId: string) {
  return call({
    path: `/api/admin/exams/${examId}/actions`,
    schema: AdminActionsResponse,
  }).then((r) => r.actions);
}

/* ------------------------------------------------------------------ */
/* Exam settings                                                       */
/* ------------------------------------------------------------------ */

/**
 * The API has had `PATCH /api/exams/:id` since the exam builder was written;
 * nothing ever called it, so every setting was frozen the moment the draft was
 * saved and a mistyped title or a wrong date meant rebuilding the paper.
 */
export function updateExam(
  id: string,
  patch: {
    title?: string;
    subject_id?: string;
    timing_mode?: "from_start" | "scheduled";
    scheduled_at?: number | null;
    available_from?: number | null;
    available_until?: number | null;
    duration_min?: number;
    min_submit_after_min?: number;
    show_answers_after_submit?: boolean;
    syllabus_visible?: boolean;
    collect_answer_sheets?: boolean;
    require_phone?: boolean;
    allow_scratchpad?: boolean;
    covers_weeks?: number[];
  },
) {
  return call({
    method: "PATCH",
    path: `/api/exams/${id}`,
    json: patch,
    schema: ExamResponse,
  }).then((r) => r.exam);
}

export function createSubject(input: { name: string; slug: string }) {
  return call({
    method: "POST",
    path: "/api/admin/subjects",
    json: input,
    schema: SubjectResponse,
  }).then((r) => r.subject);
}

export function createTopic(input: {
  subject_id: string;
  name: string;
  week_no?: number | null;
}) {
  return call({
    method: "POST",
    path: "/api/admin/topics",
    json: input,
    schema: TopicResponse,
  }).then((r) => r.topic);
}
